/**
 * ZoneTable's query helpers — the comparator and the predicates that decide what a row looks like
 * to sorting, search and filtering, plus the one number formatter every count in the table shares.
 *
 * Pure functions of their arguments, deliberately: they are wired into TanStack as inline `sortFn`
 * / `filterFn` callbacks, and keeping them free of component state is what lets them be read (and
 * reasoned about) without the table around them.
 */
import type { TableColumn, TableFilter, TableFilterValue, TableRow, TableSelection } from './types';

/**
 * Zone's comparator. TanStack's built-in sort fns (`sortFn_basic`, `sortFn_text`,
 * `sortFn_alphanumeric`) all differ from it, so it is injected as an inline `sortFn` instead.
 *
 * Note the core multiplies the result by -1 for a descending column, so blanks group at whichever
 * end "last" lands on for that direction — same as this component has always behaved.
 */
export function compareValues(a: unknown, b: unknown): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';

  if (aEmpty && bEmpty) {
    return 0;
  }
  if (aEmpty) {
    return 1;
  }
  if (bEmpty) {
    return -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  const aNumber = Number(a);
  const bNumber = Number(b);

  if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) {
    return aNumber - bNumber;
  }

  return String(a).localeCompare(String(b));
}

/** The cell text a user actually sees — drives both the default cell rendering AND the search. */
export function formattedValue(column: TableColumn, row: TableRow): string {
  const value = row[column.key];

  if (column.formatter) {
    return column.formatter(value, row);
  }

  return value === null || value === undefined ? '' : String(value);
}

export function matchesSearch(columns: TableColumn[], row: TableRow, search: string): boolean {
  if (!search) {
    return true;
  }

  const query = search.toLowerCase();

  return columns.some((column) => {
    if (column.searchable === false) {
      return false;
    }

    return formattedValue(column, row).toLowerCase().includes(query);
  });
}

/**
 * Grouped with en-US separators to match the hardcoded English copy it sits inside ("Showing",
 * "of", "selected"). Deliberately NOT the runtime's default locale: that would make the number vary
 * by machine while the words beside it stayed English, and would make these specs depend on the
 * environment's locale. One instance, since a formatter is not free to construct.
 */
const COUNT_FORMAT = new Intl.NumberFormat('en-US');

export function formatCount(value: number): string {
  return COUNT_FORMAT.format(value);
}

/** Copy a selection without aliasing the caller's array — both arms of the union. */
export function cloneSelection(value: TableSelection): TableSelection {
  if (Array.isArray(value)) {
    return [...value];
  }

  return { all: true, except: [...(value?.except ?? [])] };
}

/**
 * Local filtering. Every active filter must match for the row to survive.
 *
 * A filter that declares a `predicate` uses it; everything else falls back to set membership on
 * `row[key]`, stringified so a numeric id from a `<select>` still matches a number in the data.
 * The predicate hook exists because membership cannot express a threshold or a range — a
 * "minimum hours" control writing `['50']` would be compared against `'132'` for equality and
 * match nothing at all.
 */
export function matchesFilters(
  row: TableRow,
  filters: Record<string, TableFilterValue[]>,
  definitions: TableFilter[] = [],
): boolean {
  return Object.entries(filters).every(([key, values]) => {
    // Looked up from the definitions on each call rather than from a prebuilt map: `definitions`
    // is a handful of entries, and reading it live means a caller swapping the `filters` array
    // takes effect immediately, without the column defs (which TanStack memoizes on `columns`)
    // having to be rebuilt.
    const predicate = definitions.find((definition) => definition.key === key)?.predicate;

    if (predicate) {
      return predicate(row, values);
    }

    const value = row[key];

    if (Array.isArray(value)) {
      return value.some((entry) => values.some((candidate) => String(candidate) === String(entry)));
    }

    return values.some((candidate) => String(candidate) === String(value));
  });
}
